import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Gauge, Calendar, Fuel, Zap } from "lucide-react";
import { formatKm, formatPrice, localeFor } from "../lib/format";
import { imageUrl, PLACEHOLDER_IMAGE } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";

export default function CarCard({ car }) {
  const { t, lang } = useI18n();
  const [imgError, setImgError] = useState(false);
  const raw = car.images?.[0] || car.image;
  const src = raw ? imageUrl(raw, { thumb: true }) : PLACEHOLDER_IMAGE;

  return (
    <Link
      to={`/listing/${car.id}`}
      className="group relative block bg-[#242424] rounded-md overflow-hidden border border-white/5 hover:border-[#8f8370] transition-all duration-300"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#141414]">
        {src && !imgError ? (
          <img
            src={src}
            alt={car.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/20">{t("car.noImage")}</div>
        )}
        {car.is_upcoming ? (
          <div className="absolute top-3 left-0 bg-[#3b82f6] text-white text-[10px] font-bold tracking-widest px-4 py-1 shadow-md whitespace-nowrap">
            {t("stock.upcoming")}
          </div>
        ) : car.reserved ? (
          <div className="absolute top-3 left-0 bg-[#ef4444] text-white text-[10px] font-bold tracking-widest px-4 py-1 shadow-md whitespace-nowrap">
            {t("stock.reserved")}
          </div>
        ) : car.ready ? (
          <div className="absolute top-3 left-0 bg-[#c5a76f] text-black text-[10px] font-bold tracking-widest px-4 py-1 shadow-md whitespace-nowrap">
            {t("stock.ready")}
          </div>
        ) : null}
        <div className="absolute bottom-3 left-3">
          <div className="bg-black/75 backdrop-blur px-3 py-1 rounded text-[#c5a76f] text-sm font-semibold">
            {car.is_upcoming && car.expected_date
              ? car.expected_date
              : formatPrice(car.price, localeFor(lang))}
          </div>
        </div>
      </div>

      <div className="p-5">
        <h3 className="text-white text-[15px] font-semibold leading-snug min-h-[44px] group-hover:text-[#c5a76f] transition-colors">
          {car.title}
        </h3>
        <div className="grid grid-cols-2 gap-y-2 gap-x-3 mt-4 text-xs text-white/65">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-[#c5a76f]" />
            {car.year}
          </div>
          <div className="flex items-center gap-1.5">
            <Gauge className="w-3.5 h-3.5 text-[#c5a76f]" />
            {formatKm(car.km)}
          </div>
          <div className="flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5 text-[#c5a76f]" />
            {car.horsepower ? `${car.horsepower} ${t("car.hp")}` : "—"}
          </div>
          <div className="flex items-center gap-1.5">
            <Fuel className="w-3.5 h-3.5 text-[#c5a76f]" />
            {car.fuel}
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between text-xs">
          <span className="text-white/50">{car.body}</span>
          <span className="text-[#c5a76f] font-medium tracking-wide group-hover:translate-x-1 transition-transform">
            {t("common.view")} →
          </span>
        </div>
      </div>
    </Link>
  );
}
